import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../store/store';
import { showNotification } from '../../store/slices/notificationSlice';
import * as noteTemplateService from '../../services/noteTemplateService';
import { bulkUpdateCharacterNote } from '../../services/characterService';
import type { NoteTemplate } from '../../types/noteTemplate.types';

interface BulkEditNoteModalProps {
  open: boolean;
  onClose: () => void;
  selectedIds: string[];
  onApplied: (result: { updated: number }) => void;
}

const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string | string[] } } }).response;
    const message = response?.data?.message;
    if (Array.isArray(message)) return message.join(', ');
    if (typeof message === 'string' && message.length > 0) return message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const BulkEditNoteModal: React.FC<BulkEditNoteModalProps> = ({ open, onClose, selectedIds, onApplied }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [templates, setTemplates] = useState<NoteTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [noteValue, setNoteValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setTemplatesLoading(true);
    setTemplatesError(null);
    noteTemplateService
      .list()
      .then(rows => {
        if (!cancelled) setTemplates(rows);
      })
      .catch(err => {
        if (!cancelled) setTemplatesError(extractErrorMessage(err, 'Failed to load templates'));
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSelectedTemplateId('');
      setNoteValue('');
    }
  }, [open]);

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (templateId === '') {
      // Keep current noteValue so user can type freely
      return;
    }
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setNoteValue(template.body);
    }
  };

  const handleApply = async () => {
    if (selectedIds.length === 0) return;
    setSubmitting(true);
    try {
      const result = await bulkUpdateCharacterNote(selectedIds, noteValue || null);
      if (!mountedRef.current) return;
      onApplied(result);
    } catch (err) {
      if (!mountedRef.current) return;
      dispatch(
        showNotification({
          message: extractErrorMessage(err, 'Failed to apply note.'),
          type: 'error',
        }),
      );
    } finally {
      if (mountedRef.current) {
        setSubmitting(false);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Bulk edit note ({selectedIds.length} character(s))</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FormControl fullWidth size='small' disabled={templatesLoading || !!templatesError}>
            <InputLabel id='template-select-label'>Template</InputLabel>
            <Select
              labelId='template-select-label'
              value={selectedTemplateId}
              label='Template'
              onChange={e => handleTemplateChange(e.target.value)}
            >
              <MenuItem value=''>(none — free-form)</MenuItem>
              {templates.map(t => (
                <MenuItem key={t.id} value={t.id}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton
            onClick={() => {
              if (!open) return;
              let cancelled = false;
              setTemplatesLoading(true);
              setTemplatesError(null);
              noteTemplateService
                .list()
                .then(rows => {
                  if (!cancelled) setTemplates(rows);
                })
                .catch(err => {
                  if (!cancelled) setTemplatesError(extractErrorMessage(err, 'Failed to load templates'));
                })
                .finally(() => {
                  if (!cancelled) setTemplatesLoading(false);
                });
              return () => {
                cancelled = true;
              };
            }}
            disabled={templatesLoading}
            aria-label='Refresh templates'
          >
            {templatesLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Box>

        {templatesError && (
          <Typography color='error' variant='body2' sx={{ mb: 2 }}>
            {templatesError}
          </Typography>
        )}

        <TextField
          label='Note'
          value={noteValue}
          onChange={e => setNoteValue(e.target.value)}
          multiline
          minRows={4}
          maxRows={10}
          fullWidth
          inputProps={{ maxLength: 4000 }}
          helperText='Empty note clears the existing note on selected characters.'
          disabled={submitting}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleApply} disabled={submitting || selectedIds.length === 0}>
          {submitting ? <CircularProgress size={20} color='inherit' /> : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkEditNoteModal;
